import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  Box,
  Button,
  debounce,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Label } from '@provision/entity-types';
import { ChangeEvent, useCallback, useState } from 'react';
import { DynamicIcon, NamedLayoutBlock } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { useLabelGroups, useLabels } from 'src/graphql';
import { ConditionInput } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { DraggableLabel, LabelForm } from './draggable-label';
import { DroppableGroup, LabelGroupForm } from './droppable-group';

export const ManageLabels = () => {
  const [hasNewLabel, setHasNewLabel] = useState(false);
  const [hasNewLabelGroup, setHasNewLabelGroup] = useState(false);
  const [labelsFilters, setLabelsFilters] = useState<ConditionInput[]>([]);
  const [labelGroupsFilters, setLabelGroupsFilters] = useState<ConditionInput[]>([]);

  const partnerId = useGlobalStore.use.selectedPartnerId();

  const { labels: labelsWithoutGroups, updateLabel } = useLabels(
    {
      partnerId,
      includeOnlyOutsideGroups: true,
    },
    {
      filters: labelsFilters,
    },
  );

  const createLabel = () => {
    setHasNewLabel(true);
  };

  const createLabelGroup = () => {
    setHasNewLabelGroup(true);
  };

  const { labelGroups } = useLabelGroups(
    {
      partnerId,
    },
    {
      filters: labelGroupsFilters,
    },
  );

  const handleOnChange = debounce(({ target }: ChangeEvent) => {
    const value = (target as HTMLInputElement).value;

    if (!value) {
      setLabelsFilters([]);
      setLabelGroupsFilters([]);
      return;
    }

    const searchLabelsFilters = getSearchLabelFilters(value);
    setLabelsFilters(searchLabelsFilters);
    const searchLabelGroupsFilters = getSearchLabelGroupsFilters(value);
    setLabelGroupsFilters(searchLabelGroupsFilters);
  }, SEARCH_DEBOUNCE_DELAY);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 12,
      },
    }),
  );

  const { showSuccessAlert } = useNotifications();

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (active?.data.current && (over?.id ?? null) !== active.data.current.labelGroupId) {
        await updateLabel(
          {
            ...(active.data.current as Label),
            partnerId: partnerId!,
            labelGroupId: (over?.id as string) ?? null,
          },
          (active.data.current as Label).labelGroupId,
        );
        showSuccessAlert('Label was updated');
      }
    },
    [partnerId, showSuccessAlert, updateLabel],
  );

  return (
    <NamedLayoutBlock
      title="Labels"
      sx={{
        flex: 1,
        display: { sm: 'flex' },
      }}
    >
      <Stack direction="row" justifyContent="center" padding={5}>
        <Box
          sx={{
            width: 'max(35%, 560px)',
            maxWdith: '100%',
          }}
        >
          <Typography variant="h5">Manage Labels</Typography>
          <Typography variant="body2" mt={1}>
            Labels are versatile tags that can be created and organized into groups within your
            workspace. They can be applied to various records in the system, such as
            conversations, clients, sites, and projects. Labels help you categorize and
            organize data, making it easier to filter views, apply rules logic, and streamline
            your workflows.
          </Typography>
          <Stack mt={2} spacing={2}>
            <TextField
              placeholder="Filter labels"
              type="text"
              size="small"
              fullWidth
              variant="standard"
              onChange={handleOnChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DynamicIcon
                      name="Search"
                      sx={{
                        width: '1.5rem',
                        height: '1.5rem',
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              {hasNewLabelGroup ? (
                <LabelGroupForm
                  onSubmit={() => {
                    setHasNewLabelGroup(false);
                  }}
                  partnerId={partnerId}
                />
              ) : null}

              <Stack gap={1}>
                {labelGroups.map((group) => (
                  <DroppableGroup
                    key={group.id}
                    group={group}
                    labelsFilters={labelsFilters}
                    partnerId={partnerId}
                  />
                ))}
              </Stack>

              {hasNewLabel ? (
                <LabelForm
                  onSubmit={() => setHasNewLabel(false)}
                  labelsFilters={labelsFilters}
                  partnerId={partnerId}
                  includeOnlyOutsideGroups
                />
              ) : null}
              <Stack spacing={1}>
                {labelsWithoutGroups.map((label) => (
                  <DraggableLabel
                    key={label.id}
                    label={label}
                    labelsFilters={labelsFilters}
                    partnerId={partnerId}
                    includeOnlyOutsideGroups
                  />
                ))}
              </Stack>
            </DndContext>

            <Button
              size="small"
              onClick={createLabelGroup}
              sx={{
                width: 'fit-content',
              }}
            >
              + Group
            </Button>

            <Button
              onClick={createLabel}
              sx={{
                fontSize: '0.725rem',
                width: 'fit-content',
                alignSelf: 'end',
              }}
            >
              + Label
            </Button>
          </Stack>
        </Box>
      </Stack>
    </NamedLayoutBlock>
  );
};

function getSearchLabelFilters(value: string): ConditionInput[] {
  if (!value) {
    return [];
  }

  const searchFilters: ConditionInput[] = [
    {
      attribute: 'displayName',
      operator: 'ilike',
      value: `%${value}%`,
    },
  ];

  return searchFilters;
}

function getSearchLabelGroupsFilters(value: string): ConditionInput[] {
  if (!value) {
    return [];
  }

  const searchFilters: ConditionInput[] = [
    {
      attribute: 'labels/displayName',
      operator: 'ilike',
      value: `%${value}%`,
    },
  ];

  return searchFilters;
}
